import isEqualWith from 'lodash/isEqualWith'

import { isPointInPage, polygonFromVertices } from './markupEditorPolygon'
import type {
  AnnotationGeometry,
  MarkupPoint,
  MarkupRectangle,
  MarkupRectangleConstraints,
} from './types'

export const MIN_ANNOTATION_SIZE = 0.005
const COORDINATE_PRECISION = 1e12
export const MARKUP_COORDINATE_ROUNDING_TOLERANCE = 1 / COORDINATE_PRECISION

export type RectangleCorner =
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'
export type RectangleEdge = 'top' | 'right' | 'bottom' | 'left'

export const isValidRectangle = (rectangle: MarkupRectangle) =>
  rectangle.shape === 'rectangle' &&
  [rectangle.x_min, rectangle.y_min, rectangle.x_max, rectangle.y_max].every(
    Number.isFinite
  ) &&
  rectangle.x_min < rectangle.x_max &&
  rectangle.y_min < rectangle.y_max

export const rectanglesOverlap = (
  first: MarkupRectangle,
  second: MarkupRectangle
) =>
  isValidRectangle(first) &&
  isValidRectangle(second) &&
  first.x_min < second.x_max &&
  first.x_max > second.x_min &&
  first.y_min < second.y_max &&
  first.y_max > second.y_min

export const containsRectangle = (
  boundary: MarkupRectangle,
  rectangle: MarkupRectangle
) =>
  isValidRectangle(boundary) &&
  isValidRectangle(rectangle) &&
  rectangle.x_min >= boundary.x_min &&
  rectangle.y_min >= boundary.y_min &&
  rectangle.x_max <= boundary.x_max &&
  rectangle.y_max <= boundary.y_max

export const rectangleMeetsMinimumSize = (
  rectangle: MarkupRectangle,
  minimum: { width: number; height: number }
) =>
  rectangle.x_max - rectangle.x_min >=
    Math.max(0, minimum.width - MARKUP_COORDINATE_ROUNDING_TOLERANCE) &&
  rectangle.y_max - rectangle.y_min >=
    Math.max(0, minimum.height - MARKUP_COORDINATE_ROUNDING_TOLERANCE)

export const geometryBounds = (
  geometry: AnnotationGeometry
): MarkupRectangle => {
  if (geometry.shape === 'rectangle') return geometry
  const vertices = geometry.shape === 'point' ? [geometry] : geometry.vertices
  const xs = vertices.map(({ x }) => x)
  const ys = vertices.map(({ y }) => y)
  return {
    shape: 'rectangle',
    x_min: Math.min(...xs),
    y_min: Math.min(...ys),
    x_max: Math.max(...xs),
    y_max: Math.max(...ys),
  }
}

const roundCoordinate = (value: number) =>
  Math.round(value * COORDINATE_PRECISION) / COORDINATE_PRECISION

const rectangleAt = (
  start: MarkupRectangle,
  candidate: MarkupRectangle,
  amount: number
): MarkupRectangle => {
  const interpolate = (from: number, to: number) =>
    from === to ? from : roundCoordinate(from + (to - from) * amount)
  return {
    shape: 'rectangle',
    x_min: interpolate(start.x_min, candidate.x_min),
    y_min: interpolate(start.y_min, candidate.y_min),
    x_max: interpolate(start.x_max, candidate.x_max),
    y_max: interpolate(start.y_max, candidate.y_max),
  }
}

const orderedRectangle = (rectangle: MarkupRectangle) =>
  rectangle.shape === 'rectangle' &&
  [rectangle.x_min, rectangle.y_min, rectangle.x_max, rectangle.y_max].every(
    Number.isFinite
  ) &&
  rectangle.x_min <= rectangle.x_max &&
  rectangle.y_min <= rectangle.y_max

const lessThanInterval = (
  start: number,
  end: number,
  limit: number
): [number, number] | null => {
  if (start === end) return start < limit ? [-Infinity, Infinity] : null
  const crossing = (limit - start) / (end - start)
  return end > start ? [-Infinity, crossing] : [crossing, Infinity]
}

const overlapInterval = (
  start: MarkupRectangle,
  candidate: MarkupRectangle,
  obstacle: MarkupRectangle
) => {
  const conditions = [
    lessThanInterval(start.x_min, candidate.x_min, obstacle.x_max),
    lessThanInterval(-start.x_max, -candidate.x_max, -obstacle.x_min),
    lessThanInterval(start.y_min, candidate.y_min, obstacle.y_max),
    lessThanInterval(-start.y_max, -candidate.y_max, -obstacle.y_min),
  ]
  if (conditions.some((interval) => !interval)) return null
  const lower = Math.max(...conditions.map((interval) => interval![0]))
  const upper = Math.min(...conditions.map((interval) => interval![1]))
  return lower < upper ? ([lower, upper] as const) : null
}

const constrainRectangleTransitionOnce = (
  start: MarkupRectangle,
  candidate: MarkupRectangle,
  constraints: MarkupRectangleConstraints,
  minimumSize?: { width: number; height: number }
): { rectangle: MarkupRectangle; amount: number } | null => {
  if (!orderedRectangle(start) || !isValidRectangle(candidate)) return null
  if (
    constraints.boundary &&
    (!isValidRectangle(constraints.boundary) ||
      start.x_min < constraints.boundary.x_min ||
      start.y_min < constraints.boundary.y_min ||
      start.x_max > constraints.boundary.x_max ||
      start.y_max > constraints.boundary.y_max)
  )
    return null

  const minimum = minimumSize ?? { width: 0, height: 0 }
  const minimumWidth = Math.max(
    0,
    minimum.width - MARKUP_COORDINATE_ROUNDING_TOLERANCE
  )
  const minimumHeight = Math.max(
    0,
    minimum.height - MARKUP_COORDINATE_ROUNDING_TOLERANCE
  )
  if (
    !Number.isFinite(minimum.width) ||
    !Number.isFinite(minimum.height) ||
    minimum.width < 0 ||
    minimum.height < 0 ||
    (isValidRectangle(start) && !rectangleMeetsMinimumSize(start, minimum))
  )
    return null

  const obstacles = (constraints.obstacles ?? []).filter(isValidRectangle)
  const boundary = constraints.boundary
  const allowed = (rectangle: MarkupRectangle) =>
    isValidRectangle(rectangle) &&
    rectangleMeetsMinimumSize(rectangle, minimum) &&
    (!boundary || containsRectangle(boundary, rectangle)) &&
    obstacles.every((obstacle) => !rectanglesOverlap(rectangle, obstacle))
  if (isValidRectangle(start) && !allowed(start)) return null

  const startWidth = start.x_max - start.x_min
  const startHeight = start.y_max - start.y_min
  const candidateWidth = candidate.x_max - candidate.x_min
  const candidateHeight = candidate.y_max - candidate.y_min
  if (
    (startWidth < minimumWidth && candidateWidth < minimumWidth) ||
    (startHeight < minimumHeight && candidateHeight < minimumHeight)
  )
    return null

  const minimumEntry = Math.max(
    startWidth < minimumWidth
      ? (minimumWidth - startWidth) / (candidateWidth - startWidth)
      : 0,
    startHeight < minimumHeight
      ? (minimumHeight - startHeight) / (candidateHeight - startHeight)
      : 0
  )
  let limit = 1
  const edgeLimits: [from: number, to: number, edge: number, lower: boolean][] =
    []
  if (constraints.boundary)
    edgeLimits.push(
      [start.x_min, candidate.x_min, constraints.boundary.x_min, true],
      [start.y_min, candidate.y_min, constraints.boundary.y_min, true],
      [start.x_max, candidate.x_max, constraints.boundary.x_max, false],
      [start.y_max, candidate.y_max, constraints.boundary.y_max, false]
    )
  if (candidateWidth < minimumWidth)
    edgeLimits.push([startWidth, candidateWidth, minimumWidth, true])
  if (candidateHeight < minimumHeight)
    edgeLimits.push([startHeight, candidateHeight, minimumHeight, true])
  for (const [from, to, edge, lower] of edgeLimits) {
    if (lower ? to >= edge : to <= edge) continue
    if (lower ? from < edge : from > edge) return null
    limit = Math.min(limit, (edge - from) / (to - from))
  }

  for (const obstacle of obstacles) {
    const interval = overlapInterval(start, candidate, obstacle)
    if (!interval || interval[1] <= 0 || interval[0] >= limit) continue
    limit = Math.min(limit, Math.max(0, interval[0]))
  }

  if (minimumEntry > limit) return null
  if (limit === 0)
    return isValidRectangle(start) && allowed(start)
      ? { rectangle: start, amount: 0 }
      : null

  let result = rectangleAt(start, candidate, limit)
  if (allowed(result)) return { rectangle: result, amount: limit }

  let low = isValidRectangle(start) && allowed(start) ? 0 : minimumEntry
  let high = limit
  if (!allowed(rectangleAt(start, candidate, low))) {
    let found: number | undefined
    for (let attempt = 1; attempt <= 64; attempt += 1) {
      const offset = (limit - minimumEntry) * 2 ** -attempt
      const nearMinimum = minimumEntry + offset
      if (allowed(rectangleAt(start, candidate, nearMinimum))) {
        found = nearMinimum
        break
      }
      const nearLimit = limit - offset
      if (allowed(rectangleAt(start, candidate, nearLimit))) {
        found = nearLimit
        break
      }
    }
    if (found === undefined) return null
    low = found
  }
  for (let attempt = 0; attempt < 64; attempt += 1) {
    const middle = (low + high) / 2
    const possible = rectangleAt(start, candidate, middle)
    if (allowed(possible)) low = middle
    else high = middle
  }
  result = rectangleAt(start, candidate, low)
  return allowed(result) ? { rectangle: result, amount: low } : null
}

/** Clamps at first contact, then slides any remaining motion along clear axes. */
export const constrainRectangleTransition = (
  start: MarkupRectangle,
  candidate: MarkupRectangle,
  constraints: MarkupRectangleConstraints,
  minimumSize?: { width: number; height: number }
): MarkupRectangle | null => {
  const first = constrainRectangleTransitionOnce(
    start,
    candidate,
    constraints,
    minimumSize
  )
  if (!first || first.amount === 1) return first?.rectangle ?? null

  const horizontal = constrainRectangleTransitionOnce(
    first.rectangle,
    {
      ...first.rectangle,
      x_min: candidate.x_min,
      x_max: candidate.x_max,
    },
    constraints,
    minimumSize
  )
  const vertical = constrainRectangleTransitionOnce(
    first.rectangle,
    {
      ...first.rectangle,
      y_min: candidate.y_min,
      y_max: candidate.y_max,
    },
    constraints,
    minimumSize
  )
  const result = {
    ...first.rectangle,
    ...(horizontal && {
      x_min: horizontal.rectangle.x_min,
      x_max: horizontal.rectangle.x_max,
    }),
    ...(vertical && {
      y_min: vertical.rectangle.y_min,
      y_max: vertical.rectangle.y_max,
    }),
  }
  if (
    isValidRectangle(result) &&
    (!constraints.boundary ||
      containsRectangle(constraints.boundary, result)) &&
    (constraints.obstacles ?? []).every(
      (obstacle) => !rectanglesOverlap(result, obstacle)
    ) &&
    rectangleMeetsMinimumSize(result, minimumSize ?? { width: 0, height: 0 })
  ) {
    // Independent tangential projections can form a diagonal that crosses an
    // obstacle even when both axis-only endpoints are safe. Sweep the combined
    // segment as well so that a fast pointer event cannot pass around a corner.
    const combined = constrainRectangleTransitionOnce(
      first.rectangle,
      result,
      constraints,
      minimumSize
    )
    if (combined) return combined.rectangle
  }
  const horizontalDistance = horizontal
    ? Math.abs(horizontal.rectangle.x_min - first.rectangle.x_min) +
      Math.abs(horizontal.rectangle.x_max - first.rectangle.x_max)
    : 0
  const verticalDistance = vertical
    ? Math.abs(vertical.rectangle.y_min - first.rectangle.y_min) +
      Math.abs(vertical.rectangle.y_max - first.rectangle.y_max)
    : 0
  return horizontalDistance >= verticalDistance
    ? (horizontal?.rectangle ?? first.rectangle)
    : (vertical?.rectangle ?? first.rectangle)
}

export const rectangleFromPoints = (
  first: MarkupPoint,
  second: MarkupPoint,
  minimumSize?: { width: number; height: number },
  options: { allowOutsidePage?: boolean } = {}
): MarkupRectangle | null => {
  const rectangle: MarkupRectangle = {
    shape: 'rectangle',
    x_min: roundCoordinate(Math.min(first.x, second.x)),
    x_max: roundCoordinate(Math.max(first.x, second.x)),
    y_min: roundCoordinate(Math.min(first.y, second.y)),
    y_max: roundCoordinate(Math.max(first.y, second.y)),
  }
  // Custom minimums can land below the threshold after endpoint rounding.
  const minimumWidth =
    (minimumSize?.width ?? MIN_ANNOTATION_SIZE) -
    (minimumSize ? MARKUP_COORDINATE_ROUNDING_TOLERANCE : 0)
  const minimumHeight =
    (minimumSize?.height ?? MIN_ANNOTATION_SIZE) -
    (minimumSize ? MARKUP_COORDINATE_ROUNDING_TOLERANCE : 0)
  return isValidRectangle(rectangle) &&
    rectangle.x_max - rectangle.x_min >= minimumWidth &&
    rectangle.y_max - rectangle.y_min >= minimumHeight &&
    (options.allowOutsidePage ||
      (isPointInPage({ x: rectangle.x_min, y: rectangle.y_min }) &&
        isPointInPage({ x: rectangle.x_max, y: rectangle.y_max })))
    ? rectangle
    : null
}

export const translateGeometry = (
  geometry: AnnotationGeometry,
  start: MarkupPoint,
  current: MarkupPoint,
  allowOutsidePage = false
): AnnotationGeometry | null => {
  const delta = { x: current.x - start.x, y: current.y - start.y }
  if (geometry.shape === 'point') {
    const point = {
      x: roundCoordinate(geometry.x + delta.x),
      y: roundCoordinate(geometry.y + delta.y),
    }
    return isPointInPage(point) ? { shape: 'point', ...point } : null
  }
  if (geometry.shape === 'rectangle') {
    const next: MarkupRectangle = {
      shape: 'rectangle',
      x_min: roundCoordinate(geometry.x_min + delta.x),
      x_max: roundCoordinate(geometry.x_max + delta.x),
      y_min: roundCoordinate(geometry.y_min + delta.y),
      y_max: roundCoordinate(geometry.y_max + delta.y),
    }
    const insidePage =
      isPointInPage({ x: next.x_min, y: next.y_min }) &&
      isPointInPage({ x: next.x_max, y: next.y_max })
    return isValidRectangle(next) && (allowOutsidePage || insidePage)
      ? next
      : null
  }
  const points = geometry.vertices.map((vertex) => ({
    x: roundCoordinate(vertex.x + delta.x),
    y: roundCoordinate(vertex.y + delta.y),
  }))
  return points.some((point) => !isPointInPage(point))
    ? null
    : polygonFromVertices(points)
}

export const annotationGeometryEqual = (
  first: AnnotationGeometry,
  second: AnnotationGeometry
) => JSON.stringify(first) === JSON.stringify(second)

export const annotationGeometryEquivalent = (
  first: AnnotationGeometry,
  second: AnnotationGeometry
) =>
  isEqualWith(first, second, (firstValue, secondValue) =>
    typeof firstValue === 'number' && typeof secondValue === 'number'
      ? roundCoordinate(firstValue) === roundCoordinate(secondValue)
      : undefined
  )
